import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CategoryTag, POI, Tag } from "@poi-planner/shared";
import { sortByProximity, haversineDistanceKm } from "../services/geoService";
import { hasUsableCoordinates } from "../utils/dayMapPois";

export type SortMode = "alpha" | "timeBudget" | "recentlyAdded" | "distance";
export type TagFilterMode = "include" | "exclude";
export type TagMatchMode = "all" | "any";
/** A filter facet whose options are keyed include/exclude maps. */
export type FilterFacet =
  | "tag"
  | "source"
  | "collection"
  | "leg"
  | "trip"
  | "owner";

const FACET_STATE_KEYS = {
  tag: "tagFilters",
  source: "sourceFilters",
  collection: "collectionFilters",
  leg: "legFilters",
  trip: "tripFilters",
  owner: "ownerFilters",
} as const satisfies Record<FilterFacet, keyof PoiFilterState>;

// Bucket key for the caller's own POIs in the owner filter, used when a POI
// has no ownerId. Falling back to an empty string here would collide with
// "no current user id" (e.g. a not-yet-loaded auth state) and get filtered
// out by falsy-id guards, silently hiding "My library".
const SELF_OWNER_KEY = "__self__";

/** Map viewport bounds */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * The mutable filter/sort state of the hook. Callers that need the state to
 * outlive the component (for example the trip sidebar, which is remounted when
 * the user switches between the calendar and map views) host it themselves and
 * pass it back in through `stateBinding`.
 */
export interface PoiFilterState {
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagMatchMode: TagMatchMode;
  tagFilters: Map<string, TagFilterMode>;
  sourceFilters: Map<string, TagFilterMode>;
  collectionFilters: Map<string, TagFilterMode>;
  legFilters: Map<string, TagFilterMode>;
  tripFilters: Map<string, TagFilterMode>;
  ownerFilters: Map<string, TagFilterMode>;
  maximumDistanceKm: number | null;
}

export function createPoiFilterState(
  overrides: Partial<PoiFilterState> = {},
): PoiFilterState {
  return {
    search: "",
    sortMode: "alpha",
    sortAscending: true,
    tagMatchMode: "all",
    tagFilters: new Map(),
    sourceFilters: new Map(),
    collectionFilters: new Map(),
    legFilters: new Map(),
    tripFilters: new Map(),
    ownerFilters: new Map(),
    maximumDistanceKm: null,
    ...overrides,
  };
}

/** Externally hosted filter state plus its patch-style updater. */
export interface PoiFilterStateBinding {
  state: PoiFilterState;
  update: (updater: (prev: PoiFilterState) => Partial<PoiFilterState>) => void;
}

export interface UsePoiFiltersOptions {
  /** Initial sort mode */
  initialSortMode?: SortMode;
  /** Initial sort direction */
  initialSortAscending?: boolean;
  /** Filter by map viewport bounds (Library page) */
  boundsFilter?: {
    enabled: boolean;
    bounds: MapBounds | null;
  };
  /** Hide scheduled POIs - pass scheduled counts map (trip context) */
  hideScheduled?: {
    enabled: boolean;
    scheduledCounts: Map<string, number>;
  };
  /** Distance sorting target */
  distanceTarget?: {
    targetPoiId: string | null;
    allPois: POI[];
  };
  /** All unique source texts for autocomplete */
  allSourceTexts?: string[];
  /** Collection POI mapping for collection filter */
  collectionPoiMap?: Map<string, Set<string>>;
  /** poiId -> legIds, for the leg filter facet. */
  legPoiMap?: Map<string, string[]>;
  /** POI IDs used in each trip (for trip filter) - map of tripId -> Set of poiIds */
  tripPoiIdsMap?: Map<string, Set<string>>;
  /** Optional sidebar membership scope. Null means all POIs; an empty set means none. */
  scopeFilter?: {
    poiIds: Set<string> | null;
  };
  /** Current user id, for bucketing POIs with no ownerId as "self" in the owner filter */
  ownerContext?: {
    currentUserId: string | null;
  };
  /** Managed tag metadata and derived category definitions. */
  tagContext?: {
    tags: Tag[];
    categoryTags: CategoryTag[];
    currentUserId: string | null;
  };
  /**
   * Externally hosted filter state. When supplied the hook reads and writes it
   * instead of its own component state, and `initialSortMode` /
   * `initialSortAscending` are the binding owner's responsibility.
   */
  stateBinding?: PoiFilterStateBinding;
}

export interface TagWithCount {
  /** Stable filter criterion key. */
  tag: string;
  label: string;
  count: number;
  kind: "tag" | "category";
  ownerName?: string;
}

/** A tag option with the number of POIs it would leave if applied. */
export interface TagFacet extends TagWithCount {
  mode: TagFilterMode | undefined;
}

export interface ActiveFilterCounts {
  tag: number;
  trip: number;
  collection: number;
  leg: number;
  source: number;
  owner: number;
  distance: number;
  total: number;
}

export interface OwnerOption {
  id: string;
  name: string;
  isSelf: boolean;
}

export interface UsePoiFiltersResult {
  // State
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagMatchMode: TagMatchMode;
  tagFilters: Map<string, TagFilterMode>;
  sourceFilters: Map<string, TagFilterMode>;
  collectionFilters: Map<string, TagFilterMode>;
  legFilters: Map<string, TagFilterMode>;
  tripFilters: Map<string, TagFilterMode>;
  ownerFilters: Map<string, TagFilterMode>;
  maximumDistanceKm: number | null;

  // Setters
  setSearch: (value: string) => void;
  setSortMode: (mode: SortMode) => void;
  setSortAscending: (ascending: boolean) => void;
  setTagMatchMode: (mode: TagMatchMode) => void;
  setMaximumDistanceKm: (distanceKm: number | null) => void;
  toggleTag: (tag: string) => void;
  clearTags: () => void;
  toggleSource: (source: string) => void;
  clearSources: () => void;
  toggleCollection: (collectionId: string) => void;
  clearCollections: () => void;
  toggleLeg: (legId: string) => void;
  clearLegs: () => void;
  toggleTrip: (tripId: string) => void;
  clearTrips: () => void;
  toggleOwner: (ownerId: string) => void;
  clearOwners: () => void;
  clearAllFilters: () => void;
  /** Drops one option from a facet, whatever its include/exclude mode. */
  removeFilter: (facet: FilterFacet, key: string) => void;

  // Derived
  /** Mode-aware tag choices: progressive in All, structurally stable in Any */
  allTags: TagWithCount[];
  filteredPOIs: POI[];
  /** Distance from target for each POI (only populated when distance target is set) */
  distanceMap: Map<string, number>;
  /** All unique source texts from all POIs */
  allSourceTexts: string[];
  /** Owners represented in the POI set (self first, then alphabetical) */
  ownerOptions: OwnerOption[];
  /** Count of active filters per category */
  activeFilterCounts: ActiveFilterCounts;
  /**
   * Every tag option in the POI set with a live count: in Any mode the POIs
   * passing every non-tag filter and no excluded tag that carry the tag; in
   * All mode the current results that carry it; for an excluded tag, how
   * many POIs the exclusion is hiding.
   */
  tagFacets: TagFacet[];
  /** legId -> POIs matching every active filter except the leg filter. */
  legFacetCounts: Map<string, number>;
}

/** Check if a POI is within map bounds */
function isWithinBounds(poi: POI, bounds: MapBounds): boolean {
  if (!hasUsableCoordinates(poi)) return false;
  return (
    poi.lat >= bounds.south &&
    poi.lat <= bounds.north &&
    poi.lng >= bounds.west &&
    poi.lng <= bounds.east
  );
}

/** Generic toggle for Map<string, TagFilterMode> */
function toggleFilterMode(
  prev: Map<string, TagFilterMode>,
  key: string,
): Map<string, TagFilterMode> {
  const next = new Map(prev);
  const current = next.get(key);
  if (!current) {
    next.set(key, "include"); // off → include
  } else if (current === "include") {
    next.set(key, "exclude"); // include → exclude
  } else {
    next.delete(key); // exclude → off
  }
  return next;
}

/**
 * Reusable hook for filtering and sorting POIs.
 * Used in Sidebar (trip context) and LibraryPage (standalone).
 */
export function usePoiFilters(
  pois: POI[],
  options: UsePoiFiltersOptions = {},
): UsePoiFiltersResult {
  const {
    initialSortMode = "alpha",
    initialSortAscending = true,
    boundsFilter,
    hideScheduled,
    distanceTarget,
    collectionPoiMap,
    legPoiMap,
    tripPoiIdsMap,
    scopeFilter,
    ownerContext,
    tagContext,
    stateBinding,
  } = options;

  const [localState, setLocalState] = useState<PoiFilterState>(() =>
    createPoiFilterState({
      sortMode: initialSortMode,
      sortAscending: initialSortAscending,
    }),
  );
  const updateLocalState = useCallback(
    (updater: (prev: PoiFilterState) => Partial<PoiFilterState>) => {
      setLocalState((prev) => ({ ...prev, ...updater(prev) }));
    },
    [],
  );

  const filterState = stateBinding?.state ?? localState;
  const updateFilterState = stateBinding?.update ?? updateLocalState;
  const {
    search,
    sortMode,
    sortAscending,
    tagMatchMode,
    tagFilters,
    sourceFilters,
    collectionFilters,
    legFilters,
    tripFilters,
    ownerFilters,
    maximumDistanceKm,
  } = filterState;

  const setSearch = (value: string) => {
    updateFilterState(() => ({ search: value }));
  };

  const setSortMode = (mode: SortMode) => {
    updateFilterState(() => ({ sortMode: mode }));
  };

  const setSortAscending = (ascending: boolean) => {
    updateFilterState(() => ({ sortAscending: ascending }));
  };

  const setTagMatchMode = (mode: TagMatchMode) => {
    updateFilterState(() => ({ tagMatchMode: mode }));
  };

  const setMaximumDistanceKm = (distanceKm: number | null) => {
    updateFilterState(() => ({
      maximumDistanceKm:
        distanceKm !== null && Number.isFinite(distanceKm) && distanceKm > 0
          ? distanceKm
          : null,
    }));
  };
  const previousTargetPoiId = useRef(distanceTarget?.targetPoiId ?? null);

  useEffect(() => {
    const targetPoiId = distanceTarget?.targetPoiId ?? null;
    if (targetPoiId !== previousTargetPoiId.current) {
      previousTargetPoiId.current = targetPoiId;
      updateFilterState(() => ({ maximumDistanceKm: null }));
    }
  }, [distanceTarget?.targetPoiId, updateFilterState]);

  const tagCriteria = useMemo(() => {
    const criteria = new Map<
      string,
      {
        label: string;
        kind: "tag" | "category";
        tagIds: Set<string>;
        ownerName?: string;
      }
    >();

    if (!tagContext) {
      for (const poi of pois) {
        for (const tagId of poi.tags) {
          criteria.set(`tag:${tagId.toLowerCase()}`, {
            label: tagId,
            kind: "tag",
            tagIds: new Set([tagId]),
          });
        }
      }
      return criteria;
    }

    for (const tag of tagContext.tags) {
      const normalizedName = tag.name.trim().toLowerCase();
      const key = `tag:${normalizedName}`;
      const existing = criteria.get(key);
      if (existing) existing.tagIds.add(tag.id);
      else {
        criteria.set(key, {
          label: tag.name,
          kind: "tag",
          tagIds: new Set([tag.id]),
        });
      }
    }

    const systemCategories = new Map<
      string,
      { label: string; tagIds: Set<string> }
    >();
    for (const category of tagContext.categoryTags) {
      if (category.systemKey) {
        const key = `category-system:${category.systemKey}`;
        const existing = systemCategories.get(key);
        const isOwn =
          !category.ownerId || category.ownerId === tagContext.currentUserId;
        if (!existing) {
          systemCategories.set(key, {
            label: category.name,
            tagIds: new Set(category.tagIds),
          });
        } else {
          for (const tagId of category.tagIds) existing.tagIds.add(tagId);
          if (isOwn) existing.label = category.name;
        }
      } else {
        criteria.set(`category:${category.id}`, {
          label: category.name,
          kind: "category",
          tagIds: new Set(category.tagIds),
          ownerName:
            category.ownerId && category.ownerId !== tagContext.currentUserId
              ? category.ownerName
              : undefined,
        });
      }
    }
    for (const [key, category] of systemCategories) {
      criteria.set(key, { ...category, kind: "category" });
    }

    // Library data normally contains metadata for every canonical tag ID. Keep
    // unknown values visible as a defensive fallback while a partial snapshot
    // is loading (and for isolated component previews that only supply POIs).
    const knownTagIds = new Set(tagContext.tags.map((tag) => tag.id));
    for (const poi of pois) {
      for (const tagId of poi.tags) {
        if (knownTagIds.has(tagId)) continue;
        const key = `tag:${tagId.toLowerCase()}`;
        if (!criteria.has(key)) {
          criteria.set(key, {
            label: tagId,
            kind: "tag",
            tagIds: new Set([tagId]),
          });
        }
      }
    }
    return criteria;
  }, [pois, tagContext]);

  // Build the POI/tag relationships once per library snapshot. Category
  // criteria are unions over this inverted index, so toggling a filter stays
  // entirely in memory and never performs one lookup per POI or category.
  const criterionPoiIds = useMemo(() => {
    const poiIdsByTagId = new Map<string, Set<string>>();
    for (const poi of pois) {
      for (const tagId of poi.tags) {
        const ids = poiIdsByTagId.get(tagId) ?? new Set<string>();
        ids.add(poi.id);
        poiIdsByTagId.set(tagId, ids);
      }
    }

    const result = new Map<string, Set<string>>();
    for (const [key, criterion] of tagCriteria) {
      const ids = new Set<string>();
      for (const tagId of criterion.tagIds) {
        for (const poiId of poiIdsByTagId.get(tagId) ?? []) ids.add(poiId);
      }
      result.set(key, ids);
    }
    return result;
  }, [pois, tagCriteria]);

  // Calculate distances from target POI
  const distanceMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!distanceTarget?.targetPoiId) return map;

    const target = distanceTarget.allPois.find(
      (p) => p.id === distanceTarget.targetPoiId,
    );
    if (!target || !hasUsableCoordinates(target)) return map;

    const origin = { lat: target.lat, lng: target.lng };
    for (const poi of distanceTarget.allPois) {
      if (hasUsableCoordinates(poi)) {
        map.set(poi.id, haversineDistanceKm(origin, poi));
      }
    }
    return map;
  }, [distanceTarget?.targetPoiId, distanceTarget?.allPois]);

  // Structural filters except the leg facet, so leg option counts can be
  // taken from the same pool the results narrow from.
  const preLegFilteredPOIs = useMemo(() => {
    let result = pois;

    // Hide scheduled filter (trip context)
    if (hideScheduled?.enabled && hideScheduled.scheduledCounts) {
      result = result.filter((p) => !hideScheduled.scheduledCounts.has(p.id));
    }

    // Bounds filter (Library page - hide POIs outside map viewport)
    if (boundsFilter?.enabled && boundsFilter.bounds) {
      result = result.filter((p) => isWithinBounds(p, boundsFilter.bounds!));
    }

    // Apply the radius only when the target itself has a computed distance.
    // A POI without a distance cannot be proven to fall within the limit.
    if (
      maximumDistanceKm !== null &&
      distanceTarget?.targetPoiId &&
      distanceMap.has(distanceTarget.targetPoiId)
    ) {
      result = result.filter((poi) => {
        const distanceKm = distanceMap.get(poi.id);
        return distanceKm !== undefined && distanceKm <= maximumDistanceKm;
      });
    }

    // Text filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q));
    }

    // Source filters (multi-select with include/exclude)
    if (sourceFilters.size > 0) {
      const includeSources = [...sourceFilters.entries()]
        .filter(([_, mode]) => mode === "include")
        .map(([source]) => source.toLowerCase());
      const excludeSources = [...sourceFilters.entries()]
        .filter(([_, mode]) => mode === "exclude")
        .map(([source]) => source.toLowerCase());

      result = result.filter((p) => {
        const poiSources = (p.sources ?? []).map((s) => s.text.toLowerCase());
        // Must have ANY of the included sources (OR)
        const matchesInclude =
          includeSources.length === 0 ||
          includeSources.some((src) =>
            poiSources.some((ps) => ps.includes(src)),
          );
        // Must NOT have ANY of the excluded sources (AND NOT)
        const matchesExclude = !excludeSources.some((src) =>
          poiSources.some((ps) => ps.includes(src)),
        );
        return matchesInclude && matchesExclude;
      });
    }

    // Collection filters (multi-select with include/exclude)
    if (collectionFilters.size > 0 && collectionPoiMap) {
      const includeCollections = [...collectionFilters.entries()]
        .filter(([_, mode]) => mode === "include")
        .map(([id]) => id);
      const excludeCollections = [...collectionFilters.entries()]
        .filter(([_, mode]) => mode === "exclude")
        .map(([id]) => id);

      result = result.filter((p) => {
        // Must be in ANY of the included collections (OR)
        const matchesInclude =
          includeCollections.length === 0 ||
          includeCollections.some((colId) =>
            collectionPoiMap.get(colId)?.has(p.id),
          );
        // Must NOT be in ANY of the excluded collections (AND NOT)
        const matchesExclude = !excludeCollections.some((colId) =>
          collectionPoiMap.get(colId)?.has(p.id),
        );
        return matchesInclude && matchesExclude;
      });
    }

    // Trip filters (multi-select with include/exclude)
    if (tripFilters.size > 0 && tripPoiIdsMap) {
      const includeTrips = [...tripFilters.entries()]
        .filter(([_, mode]) => mode === "include")
        .map(([id]) => id);
      const excludeTrips = [...tripFilters.entries()]
        .filter(([_, mode]) => mode === "exclude")
        .map(([id]) => id);

      result = result.filter((p) => {
        // Must be used in ANY of the included trips (OR)
        const matchesInclude =
          includeTrips.length === 0 ||
          includeTrips.some((tripId) => tripPoiIdsMap.get(tripId)?.has(p.id));
        // Must NOT be used in ANY of the excluded trips (AND NOT)
        const matchesExclude = !excludeTrips.some((tripId) =>
          tripPoiIdsMap.get(tripId)?.has(p.id),
        );
        return matchesInclude && matchesExclude;
      });
    }

    // Owner filters (multi-select with include/exclude) - POIs with no
    // ownerId are bucketed under the current user ("My library").
    if (ownerFilters.size > 0) {
      const includeOwners = [...ownerFilters.entries()]
        .filter(([_, mode]) => mode === "include")
        .map(([id]) => id);
      const excludeOwners = [...ownerFilters.entries()]
        .filter(([_, mode]) => mode === "exclude")
        .map(([id]) => id);

      result = result.filter((p) => {
        const owner =
          p.ownerId ?? ownerContext?.currentUserId ?? SELF_OWNER_KEY;
        const matchesInclude =
          includeOwners.length === 0 || includeOwners.includes(owner);
        const matchesExclude = !excludeOwners.includes(owner);
        return matchesInclude && matchesExclude;
      });
    }

    // Sidebar scope composes with every structural/content filter above.
    const scopedIds = scopeFilter?.poiIds;
    if (scopedIds) {
      result = result.filter((p) => scopedIds.has(p.id));
    }

    return result;
  }, [
    pois,
    hideScheduled,
    boundsFilter,
    search,
    sourceFilters,
    collectionFilters,
    collectionPoiMap,
    tripFilters,
    tripPoiIdsMap,
    ownerFilters,
    ownerContext,
    scopeFilter,
    maximumDistanceKm,
    distanceTarget?.targetPoiId,
    distanceMap,
  ]);

  // Leg filters (multi-select with include/exclude). An include narrows to
  // exactly the selected legs' places; places assigned to no leg are hidden
  // until the filter is cleared. The leg pills are the only leg-filtering
  // control, so they must narrow on their own.
  const matchesLegFilters = useCallback(
    (poi: POI): boolean => {
      if (legFilters.size === 0 || !legPoiMap) return true;
      const assigned = legPoiMap.get(poi.id) ?? [];
      let hasInclude = false;
      let matchesInclude = false;
      for (const [legId, mode] of legFilters) {
        if (mode === "exclude") {
          if (assigned.includes(legId)) return false;
        } else {
          hasInclude = true;
          if (assigned.includes(legId)) matchesInclude = true;
        }
      }
      return !hasInclude || matchesInclude;
    },
    [legFilters, legPoiMap],
  );

  // Base filtered POIs - every structural filter, before the tag filter
  const baseFilteredPOIs = useMemo(
    () =>
      legFilters.size > 0 && legPoiMap
        ? preLegFilteredPOIs.filter(matchesLegFilters)
        : preLegFilteredPOIs,
    [preLegFilteredPOIs, matchesLegFilters, legFilters.size, legPoiMap],
  );

  // Tag filter - selected includes use the chosen matching mode; exclusions
  // always remove a POI independently of that mode. `ignoreExclude` lets a
  // facet ask what one exclusion is hiding.
  const matchesTagFilters = useCallback(
    (poi: POI, ignoreExclude?: string): boolean => {
      if (tagFilters.size === 0) return true;
      const matchesCriterion = (criterionKey: string) =>
        criterionPoiIds.get(criterionKey)?.has(poi.id) ?? false;
      const includeTags: string[] = [];
      for (const [tag, mode] of tagFilters) {
        if (mode === "include") includeTags.push(tag);
        // Must NOT have ANY excluded tags
        else if (tag !== ignoreExclude && matchesCriterion(tag)) return false;
      }
      return (
        includeTags.length === 0 ||
        (tagMatchMode === "all"
          ? includeTags.every(matchesCriterion)
          : includeTags.some(matchesCriterion))
      );
    },
    [tagFilters, tagMatchMode, criterionPoiIds],
  );

  // Filter and sort POIs (starts from baseFilteredPOIs, applies tag filter and sorting)
  const filteredPOIs = useMemo(() => {
    let result = baseFilteredPOIs;

    if (tagFilters.size > 0) {
      result = result.filter((p) => matchesTagFilters(p));
    }

    // Sort
    switch (sortMode) {
      case "alpha":
        result = [...result].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "timeBudget":
        result = [...result].sort((a, b) => b.timeBudget - a.timeBudget);
        break;
      case "recentlyAdded":
        result = [...result].reverse();
        break;
      case "distance": {
        const target = distanceTarget?.allPois.find(
          (p) => p.id === distanceTarget?.targetPoiId,
        );
        if (target && hasUsableCoordinates(target)) {
          const sorted = sortByProximity(result, target);
          result = sorted.map((wd) => wd.item);
        }
        break;
      }
    }

    // Sort direction (distance mode is always ascending - nearest first)
    if (!sortAscending && sortMode !== "distance") {
      result = result.reverse();
    }

    return result;
  }, [
    baseFilteredPOIs,
    sortMode,
    sortAscending,
    tagFilters,
    matchesTagFilters,
    distanceTarget,
  ]);

  // All mode progressively narrows tag choices to the current intersection.
  // Any mode keeps the pre-tag-filter pool available so disjoint tags can be
  // combined into a union.
  const allTags = useMemo(() => {
    const tagCounts = new Map<string, number>();
    const tagOptionPOIs =
      tagMatchMode === "all" ? filteredPOIs : baseFilteredPOIs;

    for (const [key] of tagCriteria) {
      const count = tagOptionPOIs.reduce((total, poi) => {
        const matches = criterionPoiIds.get(key)?.has(poi.id) ?? false;
        return total + (matches ? 1 : 0);
      }, 0);
      if (count > 0 || tagFilters.has(key)) {
        tagCounts.set(key, count);
      }
    }
    // Ensure actively filtered tags are always visible (even with 0 count)
    for (const tag of tagFilters.keys()) {
      if (!tagCounts.has(tag)) {
        tagCounts.set(tag, 0);
      }
    }
    // Sort: included first, then excluded, then unselected
    // Within each group, sort by count descending, then alphabetically
    return Array.from(tagCounts.entries())
      .map(([tag, count]) => {
        const criterion = tagCriteria.get(tag);
        return {
          tag,
          label: criterion?.label ?? tag,
          count,
          kind: criterion?.kind ?? ("tag" as const),
          ownerName: criterion?.ownerName,
        };
      })
      .sort((a, b) => {
        const aMode = tagFilters.get(a.tag);
        const bMode = tagFilters.get(b.tag);
        const aPriority = aMode === "include" ? 0 : aMode === "exclude" ? 1 : 2;
        const bPriority = bMode === "include" ? 0 : bMode === "exclude" ? 1 : 2;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return b.count - a.count || a.label.localeCompare(b.label);
      });
  }, [
    baseFilteredPOIs,
    filteredPOIs,
    tagFilters,
    tagMatchMode,
    tagCriteria,
    criterionPoiIds,
  ]);

  const tagFacets = useMemo<TagFacet[]>(() => {
    // Any mode: the non-tag pool minus anything an exclusion removes.
    const anyPool =
      tagMatchMode === "any"
        ? baseFilteredPOIs.filter((poi) =>
            [...tagFilters].every(
              ([tag, mode]) =>
                mode !== "exclude" ||
                !(criterionPoiIds.get(tag)?.has(poi.id) ?? false),
            ),
          )
        : null;
    const countIn = (list: POI[], key: string) => {
      const ids = criterionPoiIds.get(key);
      if (!ids) return 0;
      let count = 0;
      for (const poi of list) if (ids.has(poi.id)) count += 1;
      return count;
    };

    const facets: TagFacet[] = [];
    for (const [key, criterion] of tagCriteria) {
      const mode = tagFilters.get(key);
      const poiIds = criterionPoiIds.get(key);
      // Options nobody in this POI set carries are noise, unless active.
      if (!mode && !pois.some((poi) => poiIds?.has(poi.id))) continue;
      const count =
        mode === "exclude"
          ? countIn(
              baseFilteredPOIs.filter((poi) => matchesTagFilters(poi, key)),
              key,
            )
          : countIn(anyPool ?? filteredPOIs, key);
      facets.push({
        tag: key,
        label: criterion.label,
        count,
        kind: criterion.kind,
        ownerName: criterion.ownerName,
        mode,
      });
    }
    // Active filters whose criterion has gone (a deleted tag) stay removable.
    for (const [key, mode] of tagFilters) {
      if (tagCriteria.has(key)) continue;
      facets.push({ tag: key, label: key, count: 0, kind: "tag", mode });
    }
    const priority = (mode: TagFilterMode | undefined) =>
      mode === "include" ? 0 : mode === "exclude" ? 1 : 2;
    return facets.sort(
      (a, b) =>
        priority(a.mode) - priority(b.mode) ||
        b.count - a.count ||
        a.label.localeCompare(b.label),
    );
  }, [
    pois,
    baseFilteredPOIs,
    filteredPOIs,
    tagFilters,
    tagMatchMode,
    tagCriteria,
    criterionPoiIds,
    matchesTagFilters,
  ]);

  const legFacetCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!legPoiMap) return counts;
    for (const poi of preLegFilteredPOIs) {
      if (!matchesTagFilters(poi)) continue;
      for (const legId of legPoiMap.get(poi.id) ?? []) {
        counts.set(legId, (counts.get(legId) ?? 0) + 1);
      }
    }
    return counts;
  }, [preLegFilteredPOIs, legPoiMap, matchesTagFilters]);

  // Derive all unique source texts from all POIs (for autocomplete)
  const allSourceTexts = useMemo(() => {
    const sourceSet = new Set<string>();
    for (const poi of pois) {
      for (const source of poi.sources ?? []) {
        if (source.text.trim()) {
          sourceSet.add(source.text.trim());
        }
      }
    }
    return Array.from(sourceSet).sort();
  }, [pois]);

  const toggleTag = (tag: string) => {
    updateFilterState((prev) => ({
      tagFilters: toggleFilterMode(prev.tagFilters, tag),
    }));
  };

  const clearTags = () => {
    updateFilterState(() => ({ tagFilters: new Map() }));
  };

  const toggleSource = (source: string) => {
    updateFilterState((prev) => ({
      sourceFilters: toggleFilterMode(prev.sourceFilters, source),
    }));
  };

  const clearSources = () => {
    updateFilterState(() => ({ sourceFilters: new Map() }));
  };

  const toggleCollection = (collectionId: string) => {
    updateFilterState((prev) => ({
      collectionFilters: toggleFilterMode(prev.collectionFilters, collectionId),
    }));
  };

  const clearCollections = () => {
    updateFilterState(() => ({ collectionFilters: new Map() }));
  };

  const toggleLeg = (legId: string) => {
    updateFilterState((prev) => ({
      legFilters: toggleFilterMode(prev.legFilters, legId),
    }));
  };

  const clearLegs = () => {
    updateFilterState(() => ({ legFilters: new Map() }));
  };

  const toggleTrip = (tripId: string) => {
    updateFilterState((prev) => ({
      tripFilters: toggleFilterMode(prev.tripFilters, tripId),
    }));
  };

  const clearTrips = () => {
    updateFilterState(() => ({ tripFilters: new Map() }));
  };

  const toggleOwner = (ownerId: string) => {
    updateFilterState((prev) => ({
      ownerFilters: toggleFilterMode(prev.ownerFilters, ownerId),
    }));
  };

  const clearOwners = () => {
    updateFilterState(() => ({ ownerFilters: new Map() }));
  };

  const clearAllFilters = () => {
    updateFilterState(() => ({
      tagMatchMode: "all",
      tagFilters: new Map(),
      sourceFilters: new Map(),
      collectionFilters: new Map(),
      legFilters: new Map(),
      tripFilters: new Map(),
      ownerFilters: new Map(),
      maximumDistanceKm: null,
    }));
  };

  const removeFilter = (facet: FilterFacet, key: string) => {
    const stateKey = FACET_STATE_KEYS[facet];
    updateFilterState((prev) => {
      const next = new Map(prev[stateKey]);
      next.delete(key);
      return { [stateKey]: next };
    });
  };

  // Owners represented in the POI set, self first then alphabetical by name.
  const ownerOptions = useMemo<OwnerOption[]>(() => {
    const currentUserId = ownerContext?.currentUserId ?? null;
    const byId = new Map<string, OwnerOption>();
    for (const poi of pois) {
      const isSelf = !poi.ownerId || poi.ownerId === currentUserId;
      const id = poi.ownerId ?? currentUserId ?? SELF_OWNER_KEY;
      if (byId.has(id)) continue;
      byId.set(id, {
        id,
        name: isSelf ? "My library" : (poi.ownerName ?? "Shared library"),
        isSelf,
      });
    }
    return Array.from(byId.values()).sort((a, b) => {
      if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [pois, ownerContext]);

  // Count active filters per category
  const activeFilterCounts = useMemo<ActiveFilterCounts>(
    () => ({
      tag: tagFilters.size,
      trip: tripFilters.size,
      collection: collectionFilters.size,
      leg: legFilters.size,
      source: sourceFilters.size,
      owner: ownerFilters.size,
      distance: maximumDistanceKm === null ? 0 : 1,
      total:
        tagFilters.size +
        tripFilters.size +
        collectionFilters.size +
        legFilters.size +
        sourceFilters.size +
        ownerFilters.size +
        (maximumDistanceKm === null ? 0 : 1),
    }),
    [
      tagFilters,
      legFilters,
      tripFilters,
      collectionFilters,
      sourceFilters,
      ownerFilters,
      maximumDistanceKm,
    ],
  );

  return {
    // State
    search,
    sortMode,
    sortAscending,
    tagMatchMode,
    tagFilters,
    sourceFilters,
    collectionFilters,
    legFilters,
    tripFilters,
    ownerFilters,
    maximumDistanceKm,

    // Setters
    setSearch,
    setSortMode,
    setSortAscending,
    setTagMatchMode,
    setMaximumDistanceKm,
    toggleTag,
    clearTags,
    toggleSource,
    clearSources,
    toggleCollection,
    clearCollections,
    toggleLeg,
    clearLegs,
    toggleTrip,
    clearTrips,
    toggleOwner,
    clearOwners,
    clearAllFilters,
    removeFilter,

    // Derived
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    ownerOptions,
    activeFilterCounts,
    tagFacets,
    legFacetCounts,
  };
}
